import React, { useEffect, useRef } from 'react';
import { ClockCircleFilled } from '@ant-design/icons';
import { notification } from 'antd';
import { useNotification } from '../contexts/NotificationContext/NotificationContext';
import subMinutes from 'date-fns/subMinutes';
import { Appointment } from 'vl-common/src/schemas/shared';

const NOTIFICATION_AUTO_CLOSE_SEC = 10;

export function notificationKeyAppointmentEnd(appointmentId: number): string {
  return `appointment-end-soon-${appointmentId}`;
}

function formatMinutesLeftMessage(minutesLeft: number): string {
  if (minutesLeft < 1) {
    return 'You have less than a minute left in your appointment.';
  }
  if (minutesLeft === 1) {
    return 'You have 1 minute left in your appointment.';
  }
  return `You have ${minutesLeft} minutes left in your appointment.`;
}

export function useAppointmentEndNotification(
  appointment: Appointment,
  isInProgress: boolean,
  warningMinutes: number
): void {
  const { warningNotification } = useNotification();
  const openedRef = useRef(false);

  useEffect(() => {
    const apptId = appointment.appointment_id;
    const key = notificationKeyAppointmentEnd(apptId);
    if (!isInProgress) {
      openedRef.current = false;
      notification.destroy(key);
      return;
    }

    const end = appointment.end ? new Date(appointment.end) : null;
    if (end == null || Number.isNaN(end.getTime())) {
      return;
    }

    if (sessionStorage.getItem(key)) return;

    const mins = Number.isFinite(warningMinutes) && warningMinutes > 0 ? warningMinutes : 5;
    const showAtMs = subMinutes(end, mins).getTime();
    const delayMs = Math.max(0, showAtMs - Date.now());

    const timeoutId = setTimeout(() => {
      if (openedRef.current) return;

      sessionStorage.setItem(key, 'true');

      const msLeft = end.getTime() - Date.now();
      const minutesLeft = Math.max(0, Math.ceil(msLeft / 60_000));
      const message = formatMinutesLeftMessage(minutesLeft);

      openedRef.current = true;

      warningNotification({
        key,
        icon: React.createElement(ClockCircleFilled, {
          'aria-hidden': true,
          style: { color: 'var(--ant-color-warning, #faad14)' }
        }),
        message,
        placement: 'top',
        duration: NOTIFICATION_AUTO_CLOSE_SEC,
        closable: true,
        onClose: () => {
          openedRef.current = false;
        }
      });
    }, delayMs);

    return () => {
      clearTimeout(timeoutId);
      openedRef.current = false;

      if (!isInProgress) {
        notification.destroy(key);
        sessionStorage.removeItem(key);
      }
    };
  }, [appointment.appointment_id, appointment.end, isInProgress, warningMinutes]);
}
